import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { QrVincularWhatsapp } from "@/components/admin/WhatsappQr";
import { usePagePermissions } from "@/hooks/usePagePermissions";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { IS_PRODUCTION } from "@/lib/portalUrls";
import {
  horaCorta,
  RUTA_CONFIG_WHATSAPP,
  tonoWhatsapp,
  useEstadoWhatsapp,
  type EstadoConexionWhatsapp,
  type TonoWhatsapp,
} from "@/hooks/useWhatsappEvolution";

// ---------------------------------------------------------------------------
// Conexión de WhatsApp (Evolution API), en el header junto al buscador de menús.
//
// Verde parpadeando = instancia conectada. Rojo parpadeando = la sesión se cerró o
// Evolution no responde (el detalle, en el tooltip): cuando la sesión se cierra los avisos dejan de llegar
// sin error visible en ningún otro lado. Lo ve:
//   · quien puede abrir Configuraciones/Logs > WhatsApp (Evolution) (rol + "Solo Usuario A");
//   · el rol con el permiso `conectar_telefono_notificaciones` en ese submenú.
// Solo en producción (IS_PRODUCTION): en preview/dev no se pinta.
// Al hacer clic, el Super Administrador (y quien solo tiene acceso a la pantalla) abre la
// configuración. El rol con `conectar_telefono_notificaciones` no: si está desconectado ve
// el QR para volver a vincular el teléfono, y si está conectado el clic no hace nada.
// ---------------------------------------------------------------------------

const COLOR_PUNTO: Record<TonoWhatsapp, string> = {
  verde: "bg-emerald-500",
  ambar: "bg-amber-500",
  rojo: "bg-red-600",
  gris: "bg-muted-foreground/50",
};

export function PuntoWhatsapp({ tono, grande = false }: { tono: TonoWhatsapp; grande?: boolean }) {
  return (
    <span className={cn("relative flex shrink-0", grande ? "h-3.5 w-3.5" : "h-2.5 w-2.5")}>
      {(tono === "verde" || tono === "rojo") && (
        <span
          className={cn(
            "absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping motion-reduce:animate-none",
            COLOR_PUNTO[tono],
          )}
        />
      )}
      <span
        className={cn(
          "relative inline-flex h-full w-full rounded-full",
          COLOR_PUNTO[tono],
          tono === "rojo" && grande && "ring-2 ring-red-300 dark:ring-red-800",
        )}
      />
    </span>
  );
}

export function WhatsappStatusBadge() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [qrAbierto, setQrAbierto] = useState(false);
  const {
    canRead,
    canConnectNotificationPhone,
    isSuperAdmin,
    isLoading: permLoading,
  } = usePagePermissions(RUTA_CONFIG_WHATSAPP);
  // Mismo gate que la ruta: respeta "Solo Usuario A" y el apagado del submenú, que
  // `usePagePermissions` no mira (a un Super Admin le da todo).
  const { isPathAllowed, isLoading: rutaLoading } = useAllowedMenus();
  const abreConfig = canRead && !rutaLoading && isPathAllowed(RUTA_CONFIG_WHATSAPP);
  // El Super Administrador conserva el clic a la configuración aunque también tenga el permiso.
  const reconectaConQr = canConnectNotificationPhone && !isSuperAdmin;
  // Solo en producción: en preview/dev la instancia de Evolution no es la que manda los avisos y el
  // punto salía siempre en rojo, como si la de producción estuviera caída.
  const visible = IS_PRODUCTION && !permLoading && (abreConfig || reconectaConQr);
  const { data, isError } = useEstadoWhatsapp(visible);

  const alConectar = useCallback(
    (e: EstadoConexionWhatsapp) => {
      setQrAbierto(false);
      queryClient.setQueryData(["whatsapp-evolution-estado"], e);
      toast({
        title: "WhatsApp conectado",
        description: e.instancia ?? undefined,
      });
    },
    [queryClient, toast],
  );

  if (!visible || (!data && !isError)) return null;

  const tono = tonoWhatsapp(data, isError);
  const alHacerClic = () => {
    if (!reconectaConQr) {
      navigate(RUTA_CONFIG_WHATSAPP);
      return;
    }
    if (tono !== "verde") setQrAbierto(true);
  };
  // En el header solo "WhatsApp" y el color; el estado y la instancia van en el tooltip.
  const estadoTexto = isError
    ? "Sin revisar"
    : data?.estado === "conectado"
      ? "Conectado"
      : data?.estado === "conectando"
        ? "Reconectando"
        : data?.estado === "sin_configurar"
          ? "Sin configurar"
          : data?.estado === "sin_revisar"
            ? "Sin revisar"
            : data?.estado === "error"
              ? "Sin respuesta"
              : "Desconectado";

  const lineas = [
    `${estadoTexto} · instancia ${data?.instancia ?? "—"}`,
    isError
      ? "No se pudo consultar el estado."
      : data?.estado === "conectado"
        ? `Conectado desde ${horaCorta(data.desde)}`
        : data?.detalle,
    tono === "rojo" && data?.ultima_conexion && `Última vez conectado: ${horaCorta(data.ultima_conexion)}`,
    tono === "rojo" && "Mientras siga desconectado, los avisos por WhatsApp NO llegan.",
    data?.revisado_at && `Revisado: ${horaCorta(data.revisado_at)} · se revisa cada minuto`,
  ].filter(Boolean);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={alHacerClic}
            role="status"
            aria-label={`WhatsApp: ${estadoTexto}.${
              !reconectaConQr
                ? " Abrir configuración de WhatsApp"
                : tono !== "verde"
                  ? " Mostrar el QR para reconectar"
                  : ""
            }`}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium transition-colors",
              reconectaConQr && tono === "verde" ? "cursor-default" : "hover:bg-muted",
              tono === "rojo"
                ? "animate-pulse border border-red-500 bg-red-100 font-semibold text-red-700 hover:bg-red-100 motion-reduce:animate-none dark:border-red-600 dark:bg-red-950/60 dark:text-red-300"
                : tono === "ambar"
                  ? "text-amber-700 dark:text-amber-300"
                  : "text-muted-foreground",
            )}
          >
            <PuntoWhatsapp tono={tono} grande={tono === "rojo"} />
            {/* En verde basta el punto en pantallas chicas; el rojo siempre se lee. */}
            <span className={cn(tono === "verde" && "hidden lg:inline")}>WhatsApp</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-xs space-y-0.5 text-xs">
          <p className="font-semibold">Conexión de WhatsApp (Evolution API)</p>
          {lineas.map((l, i) => (
            <p key={i}>{l}</p>
          ))}
        </TooltipContent>
      </Tooltip>
      {reconectaConQr && (
        <Dialog open={qrAbierto} onOpenChange={setQrAbierto}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reconectar WhatsApp</DialogTitle>
              <DialogDescription>
                En el teléfono de notificaciones: WhatsApp → Dispositivos vinculados → Vincular un dispositivo, y
                escanea el código.
              </DialogDescription>
            </DialogHeader>
            <QrVincularWhatsapp activo={qrAbierto} onConectado={alConectar} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
